import { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../Context/AppContext';
import { RootState } from '../../Redux/Store';
import { getContentStackData } from '../../Redux/Reducers/ContentOrConfigSlice';
import { handleResetSortAndFilter, updateHomeSelectedTab } from '../../Redux/Reducers/SearchSlice';
import { useAppDispatch, useAppSelector } from '../../Hooks/Hooks';
import { fbLogEvent } from '../../Helpers/FirebaseHelper';
import { isEntitled } from '../../Utils/ContentOrConfigUtil';
import { groupByMap, moveSelectedToFront } from '../../Utils/Basic';
import { EVENT, SCREEN, TAB } from '../../Constants/AnalyticsConstants';
import NewsContainer from '../../Components/Home/NewsContainer/NewsContainer';
import NatNalComponent from '../../Components/Home/NatNalComponent/NatNalComponent';
import ResultContainer from '../../Components/ResultContainer/ResultContainer';
import './Home.scss';

const Home = () => {
  const dispatch = useAppDispatch();
  const watchedRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useContext(AppContext);
  const { watchedLoadsData, selectedHomeTab } = useAppSelector((state: RootState) => state.search);
  const { memberData } = useAppSelector((state: RootState) => state.member);
  const { contentStackData, configTableData } = useAppSelector(
    (state: RootState) => state.ContentOrConfig,
  );
  const [mainSection, setMainSection]: any = useState([]);
  const [hasLoggedEvent, setHasLoggedEvent] = useState(false);
  const [isMobileNewsTab, setIsMobileNewsTab] = useState(isMobile);

  const initMainSection = (_isMobile: boolean, newsData?: any[]) => [
    {
      title: 'News',
      value: 'news',
      webVisibility: true,
      mobileVisibility: isMobileNewsTab,
      contents: newsData ? newsData : [],
    },
    {
      title: 'NAT / NAL',
      value: 'natNal',
      webVisibility: true,
      mobileVisibility: false,
    },
    {
      title: 'Watched',
      value: 'matchedLoads',
      webVisibility: true,
      mobileVisibility: selectedHomeTab === 'matchedLoads',
    },
  ];

  useEffect(() => {
    if (isMobile && selectedHomeTab === 'matchedLoads') {
      setIsMobileNewsTab(false);
    }
  }, [selectedHomeTab]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasLoggedEvent) {
            fbLogEvent(EVENT.UE_VIEW_WATCHED_LOADS, {
              screen: SCREEN.HOME,
              tab: TAB.WATCHED,
            });
            setHasLoggedEvent(true);
          } else if (!entry.isIntersecting && hasLoggedEvent) {
            setHasLoggedEvent(false);
          }
        });
      },
      { threshold: 0.1 },
    );

    if (watchedRef.current) {
      observer.observe(watchedRef.current);
    }
    return () => {
      if (watchedRef.current) {
        observer.unobserve(watchedRef?.current);
      }
    };
  }, [hasLoggedEvent, watchedRef?.current]);

  useEffect(() => {
    setMainSection(initMainSection(isMobile));
  }, [isMobile]);

  useEffect(() => {
    !!memberData && dispatch(getContentStackData({ queryPayload: { contentType: 'news' } }));
  }, [dispatch, memberData]);

  useEffect(() => {
    const LOS = memberData?.member?.userApplicationDetails?.map((item: any) => {
      return (item?.attributes?.LineOfService + '_News')?.toUpperCase();
    });
    const _contents: any[] = Object.entries(groupByMap(contentStackData?.news, 'topic'))
      .flatMap(([key, value]) => {
        return {
          title: key,
          selected: key === 'Schneider News',
          value: key.replaceAll(' ', '_'),
          contents: value,
        };
      })
      .filter((item: any) => {
        if (item?.value === 'Schneider_News' || LOS?.includes(item?.value?.toUpperCase())) {
          return item;
        }
        return null;
      });
    if (_contents.length) {
      const updatedMainSection = initMainSection(isMobile, _contents).map((item: any) => {
        if (item.value === 'news') {
          const newsData = {
            ...item,
            contents: moveSelectedToFront(_contents, 'Schneider_News').map((content: any) => {
              return content.value !== 'Schneider_News'
                ? { ...content, title: content.title.split(' ')[0] }
                : content;
            }),
          };
          return newsData;
        }
        return item;
      });
      setMainSection(updatedMainSection);
    }
  }, [contentStackData?.news, isMobile, memberData?.member?.userApplicationDetails]);

  const renderWithSwitch = (value: any, data?: any) => {
    const NewsData = isMobile ? data : data?.filter((item: any) => item?.selected);
    switch (value) {
      case 'news':
        return <NewsContainer content={NewsData} />;
      case 'natNal':
        return <NatNalComponent />;
      case 'matchedLoads':
        return (
          <div ref={watchedRef}>
            <ResultContainer
              additionalClass="Matched"
              label="Matched Loads"
              loadPostings={watchedLoadsData?.loadPostings}
              totalRecords={watchedLoadsData?.totalRecords}
              isWatchedLoads={true}
              isMatchedLoadsHome={true}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const handleToggle = (main: string, sub?: string) => {
    dispatch(updateHomeSelectedTab(main));
    if (main === 'matchedLoads') {
      dispatch(handleResetSortAndFilter());
    }
    if (!sub) {
      setMainSection(
        mainSection?.map((item: any) => {
          return {
            ...item,
            mobileVisibility: item?.value === main,
          };
        }),
      );
    } else {
      setMainSection(
        mainSection?.map((item: any) => {
          return {
            ...item,
            contents:
              item.value !== main
                ? item?.contents
                : item?.contents?.map((content: any) => {
                    return {
                      ...content,
                      selected: content?.value === sub,
                    };
                  }),
          };
        }),
      );
    }
  };

  return (
    <div className="Home">
      <div className="Home_Toggle">
        {mainSection?.map((item: any) => {
          if (isMobile) {
            if (
              !isEntitled(
                memberData?.member,
                configTableData?.FPO_APP_CONFIG?.APP_INIT,
                'home',
                item?.value,
              )
            )
              return null;
          }
          if (!isMobile) {
            const newsSubSections = item?.value === 'news' && item?.contents;
            if (
              !newsSubSections ||
              !isEntitled(
                memberData?.member,
                configTableData?.FPO_APP_CONFIG?.APP_INIT,
                'home',
                'news',
              )
            )
              return null;
            return newsSubSections?.map((newsSubSection: any) => {
              return (
                <button
                  key={newsSubSection?.value}
                  className={`section ${
                    newsSubSection?.selected ? 'selected' : ''
                  } ${newsSubSection?.value}`}
                  onClick={() => {
                    handleToggle(item?.value, newsSubSection?.value);
                  }}
                >
                  {newsSubSection?.title}
                </button>
              );
            });
          }
          return (
            <button
              key={item?.value}
              className={`section ${item?.mobileVisibility ? 'selected' : ''} ${item?.value}`}
              onClick={() => {
                handleToggle(item?.value);
              }}
            >
              {item?.title}
            </button>
          );
        })}
      </div>

      {mainSection?.map((item: any) => {
        if (
          !isEntitled(
            memberData?.member,
            configTableData?.FPO_APP_CONFIG?.APP_INIT,
            'home',
            item?.value,
          )
        ) {
          return null;
        }
        if ((isMobile && item?.mobileVisibility) || (!isMobile && item?.webVisibility)) {
          return (
            <div
              className={`Home_Main_Section ${item?.value}`}
              key={item?.value}
              id="Home_Main_Section"
            >
              {renderWithSwitch(item?.value, item?.contents)}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

export default Home;
